import { PdsWidget, StringBoolean } from './pds-widget';
import { pdsAccessibility } from './pds-accessibility';
import { textSpanContainsPosition } from 'typescript';
const escKey = 27; //esc
const tabKey = 9; // tab
const enterKey = 13; // enter
export class PdsDialog extends PdsWidget {
  is = 'pds-dialog';

  // If escape is pushed we fire an event that signals to remove the overlay
  onKeyDown: (event: KeyboardEvent) => void;

  modalTitle = '';

  //pds-dialog attribute to differentiate between each dialog window.
  name = '';
  dataReturnFocus: string;
  returnFocusCallback: () => string | HTMLElement;

  dataIsDrawerMode = 'false';
  dataIsLarge: StringBoolean;

  properties = {
    name: {
      type: String,
      value: ''
    },
    dataReturnFocus: {
      type: String,
      value: ''
    },
    dataIsDrawerMode: {
      type: String,
      value: 'false'
    },
    dataIsLarge: {
      type: String,
      value: 'false'
    }
  };

  ready(): void {
    super.ready();
    /**
     * In the beginning of the polymer hook I want to add a event listener to the whole window
     * that listens for the escape key to be pressed. if the escape key is pressed fire an event.
     */
    this.onKeyDown = (event: KeyboardEvent): void => {
      if (event.keyCode === escKey) { //esc
        this.closeDialog(event);
      }
    };
  }

  /**
   * When the element is attached a few things happen: EACH PDS MODAL MUST HAVE A UNIQUE NAME and a reference to the element
   * that made the dialog pop up. (done by adding data-return-focus to <pds-dialog data-return-focus="")>
   * 1) If the pds-dialog doesnt have a name attribute, check if there are more than one <pds-dialog>
   * 2) The modal is in focus for accessibility purposes
   * 2) Inside the <pds-dialog> selector, the first h4 will be regarded as the title.
   * 3) <section> tags from the local dom will be the body
   * 4) <footer> tags from the local dom will be the footer of the modal
   *
   * THIS IS NOT TO BE USED IN A SHOW/HIDE CLASS
   */

  attached(): void {
    // angular has an issue where it will momentarily display content on the page before moving it to the modal overlay
    // we need to hide the content before until it's all in the right place
    this.classList.add('pds-is-hidden');

    if (this.dataIsDrawerMode === 'true') {
      let modalElement: HTMLElement = <HTMLElement>this.querySelector('.pds-modal');
      if (modalElement != null) {
        modalElement.classList.add('pds-drawer-mode');
      }
    }

    setTimeout(() => {
      super.attached();
      window.addEventListener('keydown', this.onKeyDown);
      // when the dialog is open I want to focus on the modal.
      let myModal = (<HTMLElement>this.querySelector('.pds-modal'));
      let myFirstInputElement;
      let myLastElement;

      let myHeaderElement = <HTMLElement>this.querySelector('header');
      let myTitleElement = <HTMLElement>this.querySelector('h4');
      let myContentElement = <HTMLElement>this.querySelector('section');
      let myFooter = <HTMLElement>this.querySelector('footer');

      /**
       * IF there is a title, then it must be the first element
       */
      if (myTitleElement != null) {
        myTitleElement.classList.add('pds-modal-title');
        myTitleElement.setAttribute('tabindex', '0');
        this.modalTitle = myTitleElement.innerText;
        myHeaderElement.appendChild(myTitleElement);
      }

      /**
       * if there is content, set the class add a role of main
       * and append the content to the modal
       */
      if (myContentElement != null) {
        myContentElement.classList.add('pds-modal-body');
        myContentElement.setAttribute('role', 'main');
        myModal.appendChild(myContentElement);
      }

      /**
       * if there is a footer check if there are contents
       */

      if (myFooter != null) {
        myFooter.classList.add('pds-modal-footer');
        myModal.appendChild(myFooter);
        if (myFooter.childElementCount >= 1) {
          myLastElement = this.pds.utils.findLastNode(myFooter);
        }
      }

      if (myLastElement == null) {
        myLastElement = this.pds.utils.findLastNode(myModal);
        myLastElement.setAttribute('tabindex', '0'); // this is incase the last element is not tabable
      }

      // in case the section of the modal forces the loader to show up..
      // focus on the first element when we get to the modal
      // if the modal doesnt force a loader to appear, we want to clear the last marked element.
      if (document.querySelector('pds-main-loader') != null) {
        this.pds.utils.markFocusableElement(myFirstInputElement);
      }

      // remove the pds-is-hidden class so that the content will be displayed only after being attached to the modal overlay
      //PDS-1210 this needed to be moved to before the focus event so the modal displays first and then can focus.
      this.classList.remove('pds-is-hidden');

      let allTabbableElementsInHeader = this.pds.utils.getAllTabbableElements(myHeaderElement);
      if (allTabbableElementsInHeader != null && allTabbableElementsInHeader.length > 0) {
        myFirstInputElement = allTabbableElementsInHeader[0];
        (<HTMLElement>myFirstInputElement).focus();
      }
      this.manageFocus(myModal, myHeaderElement, myFirstInputElement, myLastElement);

    });
  }

  /**
   * 2 Scenarios happen here:
   * 1) take a copy of the initial first and last element so we can listen for changes inside the modal
   * 2) when the user presses tab we want to recalculate the first and last element
   * @param myModal
   * @param myHeader
   * @param myFirstElement
   * @param myLastElement
   */

  manageFocus(myModal: HTMLElement, myHeaderElement: HTMLElement, myFirstElement: HTMLElement, myLastElement: HTMLElement): void {
    /**
     * take a copy of the first and last element so we can track changes here.
     */
    let copyOfFirstElement = myFirstElement;
    let copyOfLastElement = myLastElement;

    myModal.addEventListener('keydown', (event) => {
      copyOfLastElement = this.pds.utils.findLastNode(myModal);
      copyOfLastElement.setAttribute('tabindex', '0'); // this is incase the last element is not tabable
      if (event.keyCode === tabKey) { // tab and shift + tab
        if (!event.shiftKey && event.target === copyOfLastElement) {
          copyOfFirstElement.focus(); // focus on the header or close icon within header
          event.preventDefault();
          // if youre target is either the first element or the header as a whole.
        } else if (event.shiftKey && ((event.target === copyOfFirstElement) || (event.target === myHeaderElement))) {
          copyOfLastElement.focus();
          event.preventDefault();
        }
      }
      /**
       * todo: remove this code
       */
      if (event.keyCode === escKey) {
        this.closeDialog(event);
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }

  /**
   * When the element is detached, the focus should be on either a single element that triggered the modal to open,
   * or another element based on some action that causes the modal to close, and some decision to be made.
   * If there is only one element to return focus to, we expect that to be in the data-return-focus attribute.
   * If there is a decision to return focus to another element, based upon some decision that occurs after a button is clicked,
   * it would be beneficial to handle return the focusable element using the returnFocusCallback.
   */
  detached(): void {
    super.detached();
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.dataReturnFocus != null && this.dataReturnFocus.trim().length !== 0) {
      let elementToFocus = <HTMLElement>document.querySelector(this.dataReturnFocus);
      if (elementToFocus != null) {
        elementToFocus.focus();
      } else if (<HTMLElement>document.activeElement != null) {
        (<HTMLElement>document.activeElement).focus();
      }
      return;
    } else if (this.returnFocusCallback != null) {
      let returnSelector = this.returnFocusCallback();
      if (returnSelector instanceof HTMLElement) {
        returnSelector.focus();
        return;
      } else if (returnSelector != null && returnSelector.trim().length > 0) {
        let returnFocusElement = <HTMLElement>document.querySelector(returnSelector);
        if (returnFocusElement) {
          returnFocusElement.focus();
          return;
        }
      }
    } else if (this.pds.utils.peekLastMarkedElement() != null) {
      let myLastElement = this.pds.utils.getLastMarkedElement();
      if (myLastElement != null) {
        myLastElement.focus();
        return;
      }
    }
    console.error('Selector from data-return-focus did not find any elements, or a callback function was not specified for [returnFocusCallback]. Please set the data-return-focus attribute like: <pds-dialog data-return-focus="#id-of-button-that-opened-dialog"> or the callback function like <pds-dialog [returnFocusCallback]="returnFocusToElement"');
  }

  /**
   * This function gets called when
   * 1) The close button is clicked on the modal
   * 2) When escape is pressed: there is an event listener
   *    that gets added when the polymer hook ready() gets called
   * 3) When the user clicks outside of the modal
   *    which leads to a hideDialog event to be fired.
   * @param event
   */

  closeDialog(event?: any): void {
    this.fire('pdsRemoveDialog', true);
  }

  messageKeysUpdated(): void {

  }
}
